import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker'; // Assuming react-datepicker is used for date picking
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import 'chart.js/auto';
import { ChartOptions } from 'chart.js';
import { getGasConsumption } from 'services/gasConsumptionService';
import { getTotalCost } from 'services/totalCostService';
import { GasConsumptionData, TrendsType } from 'types';
import { useNavigate } from 'react-router-dom';
import {
  toLocalMonthDay, toLocalYearMonthDay, toLocalYearMonth, toLocalYear,
  toLocalDate, fromLocalDate,
  currentLocalYear, nextLocalYear,
  currentLocalMonth, nextLocalMonth
} from 'utils/localTime';

const getDateSequence = (start: Date, end: Date, gen: (i: number) => Date) => {
  const startMs = start.getTime();
  const endMs = end.getTime();
  const times = [];
  for (let d = 0; ; ++d) {
    const date = gen(d);
    const dateMs = date.getTime();
    if (dateMs >= endMs) {
      break;
    }
    if (dateMs >= startMs) {
      times.push(date);
    }
  }
  return times;
};


const fromLocalDay = (year: number, monthIndex: number, day: number): Date => {
  const date = new Date(year, monthIndex, day);
  return fromLocalDate(
    date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0
  );
};

const monthAround = (time: Date): [Date, Date] =>
  [currentLocalMonth(time), nextLocalMonth(time)];

const getDays = ([start, end]: [Date, Date]) => {
  const [year, monthIndex, day] = toLocalDate(start);
  return getDateSequence(
    start, end, i => fromLocalDay(year, monthIndex, day + i)
  );
};

const weeksAround = (time: Date): [Date, Date] => {
  const weekStart = (year: number, monthIndex: number) => fromLocalDay(
    year, monthIndex, 1 - new Date(year, monthIndex, 1).getDay()
  );

  const [year, monthIndex] = toLocalDate(time);
  const quarterIndex = monthIndex - (monthIndex % 3);
  return [weekStart(year, quarterIndex), weekStart(year, quarterIndex + 3)];
};

const getWeeks = ([start, end]: [Date, Date]) => {
  const [year, monthIndex, day] = toLocalDate(start);
  return getDateSequence(
    start, end, i => fromLocalDay(year, monthIndex, day + 7 * i)
  );
};

const monthsAround = (time: Date): [Date, Date] =>
  [currentLocalYear(time), nextLocalYear(time)];

const getMonths = ([start, end]: [Date, Date]) => {
  const [year, monthIndex] = toLocalDate(start);
  return getDateSequence(
    start, end, i => fromLocalDay(year, monthIndex + i, 1)
  );
};

const yearsAround = (time: Date): [Date, Date] => {
  const [year] = toLocalDate(time);
  const decade = year - (year % 10);
  return [fromLocalDay(decade, 0, 1), fromLocalDay(decade + 10, 0, 1)];
};

const getYears = ([start, end]: [Date, Date]) => {
  const [year] = toLocalDate(start);
  return getDateSequence(start, end, i => fromLocalDay(year + i, 0, 1));
};

const navigation = {
  day: {
    groupBy: 'd',
    getRange: monthAround,
    getTimes: getDays,
    format: toLocalMonthDay
  },
  week: {
    groupBy: 'w',
    getRange: weeksAround,
    getTimes: getWeeks,
    format: toLocalYearMonthDay
  },
  month: {
    groupBy: 'm',
    getRange: monthsAround,
    getTimes: getMonths,
    format: toLocalYearMonth
  },
  year: {
    groupBy: 'y',
    getRange: yearsAround,
    getTimes: getYears,
    format: toLocalYear
  }
};

const closest = (array: number[], target: number) =>
  array.reduce(
    (acc, value, i) => {
      const di = Math.abs(target - value);
      return di < acc[0] ? [di, i] : acc;
    },
    [Number.POSITIVE_INFINITY, -1]
  )[1];

const getNAValues = (dates: Date[]) =>
  Array.from<number>({length: dates.length});

const getValues = (dates: Date[], data: GasConsumptionData[]) => {
  const a = getNAValues(dates);
  const datesMs = dates.map((t) => t.getTime());
  for (const { time, gas_consumption } of data) {
    const k = closest(datesMs, new Date(time).getTime());
    if (a[k] === undefined) {
      a[k] = 0;
    }
    a[k] += gas_consumption;
  }
  return a;
};

const total = (a: number[]) =>
  a.reduce((a, v) => (v !== undefined ? a + v : a), 0);

const GasConsumptionGraph: React.FC<{
  startTime: Date;
  view: TrendsType;
}> = ({ startTime, view }) => {
  const navigate = useNavigate();

  const [currentTime, setCurrentTime] = useState(startTime);
  const [data, setData] = useState({
    loading: false,
    labels: [] as string[],
    projected: [] as number[],
    actual: [] as number[],
    totalGasConsumption: '',
    totalProjectedGasConsumption: '',
    totalGasCost: ''
  });

  const fetchData = useCallback(async () => {
    if (view === TrendsType.Hourly) return;

    const { groupBy, getRange, getTimes, format } = navigation[view];

    console.log('currentTime', currentTime);
    const range = getRange(currentTime);
    console.log('range', range);
    const dates = getTimes(range);
    console.log('dates', dates);

    const labels = dates.map(format);
    setData({
      loading: true,
      labels,
      projected: getNAValues(dates),
      actual: getNAValues(dates),
      totalGasConsumption: '',
      totalProjectedGasConsumption: '',
      totalGasCost: ''
    });

    const { gas_consumption_projection, gas_consumption } =
      await getGasConsumption(...range, groupBy);

    const projected = getValues(dates, gas_consumption_projection);
    const actual = getValues(dates, gas_consumption);

    const totalGasCost = (await getTotalCost(...range)).total_cost;

    setData({
      loading: false,
      labels,
      projected,
      actual,
      totalGasConsumption: `${total(actual).toFixed(2)} MCF`,
      totalProjectedGasConsumption: `${total(projected).toFixed(2)} MCF`,
      totalGasCost: `$${totalGasCost.toFixed(2)}`
    });
  }, [view, currentTime]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewChange = (newView: 'day' | 'week' | 'month' | 'year') => {
    navigate(`/trends/${newView}`);
  };

  const handleDateChange = (direction: 'prev' | 'next') => {
    if (view === TrendsType.Hourly) return;
    const { getRange } = navigation[view];

    const getRangeMs = (time: Date) => getRange(time).map((t) => t.getTime());

    const [startMs, endMs] = getRangeMs(currentTime);

    const [newStartMs, newEndMs] = getRangeMs(
      new Date(
        (startMs + endMs) / 2 +
          (direction === 'prev' ? startMs - endMs : endMs - startMs)
      )
    );
    setCurrentTime(new Date((newStartMs + newEndMs) / 2));
  };

  const chartData = {
    labels: data.labels,
    datasets: [
      {
        label: 'Projected gas consumption (MCF)',
        data: data.projected,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Actual gas consumption (MCF)',
        data: data.actual,
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    color: 'white',
    scales: {
      x: {
        ticks: {
          color: 'white',
        },
      },
      y: {
        ticks: {
          color: 'white',
        },
      },
    },
  };

  return (
    <>
      <div className="mb-4 flex w-full grow items-center justify-between">
        <button
          onClick={() => handleDateChange('prev')}
          disabled={false}
          className="mr-10 mt-8 h-72 bg-blue-500 p-4 text-4xl text-background"
        >
          {'<'}
        </button>
        <div className="flex h-[90%] w-full flex-col">
          <div className="mb-4 flex items-center justify-between">
            <DatePicker
              selected={currentTime}
              onChange={(date: Date) => setCurrentTime(date)}
              className="bg-lightblue rounded px-2 py-1 text-black"
              dateFormat="MM/yyyy"
              showMonthYearPicker
            />
            <div className="flex-grow"></div>
            {data.loading ? (<span>Loading...</span>) : (<span></span>)}
            <div className="flex-grow"></div>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => handleViewChange('day')}
                className={`rounded px-4 py-2 ${view === TrendsType.Days ? 'bg-blue-600 text-white' : 'bg-gray-300 text-black'}`}
              >
                Days
              </button>
              <button
                onClick={() => handleViewChange('week')}
                className={`rounded px-4 py-2 ${view === 'week' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-black'}`}
              >
                Weeks
              </button>
              <button
                onClick={() => handleViewChange('month')}
                className={`rounded px-4 py-2 ${view === 'month' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-black'}`}
              >
                Months
              </button>
              <button
                onClick={() => handleViewChange('year')}
                className={`rounded px-4 py-2 ${view === 'year' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-black'}`}
              >
                Years
              </button>
            </div>
          </div>

          <div className="relative w-[100%] grow">
            <Line data={chartData} options={options} />
          </div>
        </div>
        <button
          onClick={() => handleDateChange('next')}
          disabled={false}
          className="ml-10 mt-8 h-72 bg-blue-500 p-4 text-4xl text-background"
        >
          {'>'}
        </button>
      </div>
      <div className="totals mt-4">
        <p>
          Total gas consumption of period selected:{' '}
          {data.totalGasConsumption}
        </p>
        <p>
          Total projected gas consumption of period selected:{' '}
          {data.totalProjectedGasConsumption}
        </p>
        <p>
          Total gas cost of period selected:{' '}
          {data.totalGasCost}
        </p>
      </div>
    </>
  );
};

export default GasConsumptionGraph;
